import { makeAutoObservable } from "mobx";
import { useContext } from "react";
import React from "react";
import { ApolloClient, gql } from "@apollo/client";
import { LogoutDocument } from "../gql/graphql";
import { identifyPosthogUser, resetPosthogUser } from "../utils/posthog";

// Subset of UserEntitlements that the floorplanner cares about. Mirrors the
// server schema (`EnterpriseLimits`) — only the limits we actively enforce in
// the client are listed here. A `null` value means "unlimited" per the server
// contract.
export type UserEntitlementsLimits = {
  maxFloorplans: number | null;
  maxFloorplanShapes: number | null;
};

export type UserEntitlements = {
  plan: string;
  isActive: boolean;
  limits: UserEntitlementsLimits;
  aiCredits?: {
    monthlyLimit: number | null;
    used: number;
    remaining: number | null;
    resetAt: string;
    unlimited: boolean;
    topupCredits: number;
  };
};

const CurrentUserWithFeatureFlagsDocument = gql`
  query currentUserWithFeatureFlags {
    currentUser {
      id
      email
      role
      entitlements {
        plan
        isActive
        limits {
          maxFloorplans
          maxFloorplanShapes
        }
        aiCredits {
          monthlyLimit
          used
          remaining
          resetAt
          unlimited
          topupCredits
        }
        featureFlags {
          floorplannerCatalogMaterials
          floorplannerProduct3DAssets
          shareDocuments
        }
      }
    }
  }
`;

const ValidateSessionWithFeatureFlagsDocument = gql`
  mutation validateSessionWithFeatureFlags {
    validateSession {
      id
      role
      entitlements {
        plan
        isActive
        limits {
          maxFloorplans
          maxFloorplanShapes
        }
        aiCredits {
          monthlyLimit
          used
          remaining
          resetAt
          unlimited
          topupCredits
        }
        featureFlags {
          floorplannerCatalogMaterials
          floorplannerProduct3DAssets
          shareDocuments
        }
      }
    }
  }
`;

class UserStore {
  client: ApolloClient<any> | undefined;
  userId = undefined as string | undefined;
  userEmail: string | undefined;
  userRole: string | undefined;
  entitlements: UserEntitlements | undefined;
  featureFlags: Record<string, boolean | undefined> = {};

  constructor() {
    makeAutoObservable(this);
  }

  initialize(client: ApolloClient<any>) {
    this.client = client;
    this.validateSession();
  }

  get isAuthenticated() {
    return !!this.userId;
  }

  get isAdmin() {
    return !!this.userRole && this.userRole.toLowerCase() === 'admin';
  }

  /**
   * Floorplan count cap. `null` means unlimited (paid plans). The cap currently
   * applies only to freemium users (no subscription + no activated trial).
   */
  get maxFloorplans(): number | null {
    return this.entitlements?.limits?.maxFloorplans ?? null;
  }

  /**
   * Per-floorplan shape count cap (walls + symbols + singleLines + rulerLines).
   * `null` means unlimited (paid plans).
   */
  get maxFloorplanShapes(): number | null {
    return this.entitlements?.limits?.maxFloorplanShapes ?? null;
  }

  /**
   * Convenience flag: true when the current user is subject to at least one
   * floorplan-related cap. Used to show paywall affordances in the UI.
   */
  get isFreemiumUser(): boolean {
    return this.maxFloorplans != null || this.maxFloorplanShapes != null;
  }

  setUserId = (id: string | undefined) => {
    this.userId = id;
  };

  signOut = async () => {
    this.setUser(undefined);
    await this.client?.cache.reset();
    // Optionally, call an API endpoint to invalidate the session on the server
    try {
      await this.client?.mutate({
        mutation: LogoutDocument,
        fetchPolicy: "network-only",
      });
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  setUser = (user?: {
    id: string;
    email: string;
    role: string;
    entitlements?: (UserEntitlements & {
      featureFlags?: Record<string, boolean | null | undefined> | null;
    }) | null;
  }) => {
    if (user) {
      this.userId = user.id;
      this.userEmail = user.email || undefined;
      this.userRole = user.role;
      const rawEntitlements = user.entitlements;
      this.entitlements = rawEntitlements
        ? {
            plan: rawEntitlements.plan,
            isActive: rawEntitlements.isActive,
            limits: rawEntitlements.limits,
            aiCredits: rawEntitlements.aiCredits,
          }
        : undefined;
      this.featureFlags = Object.fromEntries(
        Object.entries(rawEntitlements?.featureFlags ?? {}).map(
          ([key, value]) => [key, value ?? undefined],
        ),
      );

      // Identify user in PostHog for behavioral analytics
      identifyPosthogUser(user.id, {
        email: user.email || undefined,
        role: user.role,
        plan: rawEntitlements?.plan,
      });
    } else {
      this.userId = undefined;
      this.userEmail = undefined;
      this.userRole = undefined;
      this.entitlements = undefined;
      this.featureFlags = {};

      // Reset PostHog user identification on logout / cleared session
      resetPosthogUser();
    }
  };

  validateSession = async () => {
    if (!this.client) {
      return;
    }
    try {
      const { data } = await this.client.mutate({
        mutation: ValidateSessionWithFeatureFlagsDocument,
        fetchPolicy: "network-only",
      });
      if (data.validateSession) {
        const entitlements = data.validateSession.entitlements
          ? {
              plan: data.validateSession.entitlements.plan,
              isActive: data.validateSession.entitlements.isActive,
              limits: {
                maxFloorplans:
                  data.validateSession.entitlements.limits?.maxFloorplans ??
                  null,
                maxFloorplanShapes:
                  data.validateSession.entitlements.limits
                    ?.maxFloorplanShapes ?? null,
              },
              featureFlags: data.validateSession.entitlements.featureFlags,
              aiCredits: data.validateSession.entitlements.aiCredits,
            }
          : undefined;
        this.setUser({
          id: data.validateSession.id,
          email: this.userEmail || '',
          role: data.validateSession.role,
          entitlements,
        });
      } else {
        this.setUser(undefined);
      }
    } catch (error) {
      console.error("Failed to validate current session", error);
      this.setUser(undefined);
    }
  };

  fetchCurrentUser = async () => {
    if (!this.client) {
      return;
    }
    try {
      const { data } = await this.client.query({
        query: CurrentUserWithFeatureFlagsDocument,
        fetchPolicy: "network-only", // Ensure the request is made to the server
      });
      const u = data.currentUser;
      if (!u) {
        this.setUser(undefined);
        return;
      }
      const entitlements = u.entitlements
        ? {
            plan: u.entitlements.plan,
            isActive: u.entitlements.isActive,
            limits: {
              maxFloorplans: u.entitlements.limits?.maxFloorplans ?? null,
              maxFloorplanShapes:
                u.entitlements.limits?.maxFloorplanShapes ?? null,
            },
            featureFlags: u.entitlements.featureFlags,
            aiCredits: u.entitlements.aiCredits,
          }
        : undefined;
      this.setUser({
        id: u.id,
        email: u.email,
        role: u.role,
        entitlements,
      });
    } catch (error) {
      console.error("Failed to fetch current user", error);
      this.setUser(undefined);
    }
  };
}

gql`
  mutation validateSession {
    validateSession {
      id
      role
      entitlements {
        plan
        isActive
        limits {
          maxFloorplans
          maxFloorplanShapes
        }
        aiCredits {
          monthlyLimit
          used
          remaining
          resetAt
          unlimited
          topupCredits
        }
        featureFlags {
          floorplannerCatalogMaterials
          floorplannerProduct3DAssets
          shareDocuments
        }
      }
    }
  }
`;

gql`
  query currentUser {
    currentUser {
      id
      email
      role
      entitlements {
        plan
        isActive
        limits {
          maxFloorplans
          maxFloorplanShapes
        }
        aiCredits {
          monthlyLimit
          used
          remaining
          resetAt
          unlimited
          topupCredits
        }
        featureFlags {
          floorplannerCatalogMaterials
          floorplannerProduct3DAssets
          shareDocuments
        }
      }
    }
  }
`;

gql`
  mutation logout {
    logout
  }
`;

export const userStore = new UserStore();
export const userStoreContext =
  React.createContext<UserStore>(userStore);
export const useUserStore = () => useContext(userStoreContext);
